import { useEffect, useState } from 'react'
import { Form } from 'react-bootstrap'
import { useFormik } from 'formik'
import { Text, SelectBox, Email, Password, File, Number, SelectStatusBox, DatePicker } from 'components/input_fields'
import { AddUserSchema } from './ValidationSchema'
import { SaveButton } from 'components/buttons'
import { Loader } from 'components/loader'
import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { userRoleList } from '../../../../redux/slices/settings'
import { addUser } from '../../../../redux/slices/users'
import { toggleAddModal, toggleBool } from '../../../../redux/slices/globalSlice'
import { addUpdateHandler } from 'utils'
import { useTranslation } from 'react-i18next'

export default function AddUserForm() {

    const [loading, setLoading] = useState(true)
    const [spinner, setSpinner] = useState(false)
    const [showPassword, setShowPassword] = useState(false)
    const [showConfirmPassword, setShowConfirmPassword] = useState(false)
    const { t } = useTranslation()
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const role = useSelector(state => state?.userRole?.list)

    const role_option = role?.map(val => (
        { "value": val.id, "label": val.role_name }
    ))

    const defaultRoleId = role?.find(val => val.role_name === 'Xpress Admin')?.id || ''

    const gender_option = [
        { "value": 'Male', "label": 'Male' },
        { "value": 'Female', "label": 'Female' },
        { "value": 'Other', "label": 'Other' },
    ]

    const initialValues = {
        role_id: defaultRoleId,
        fname: '',
        lname: '',
        gender: '',
        username: '',
        password: '',
        confirmPassword: '',
        mobile: '',
        email: '',
        dob: '',
        profile: '',
        status: 'Active'
    }

    const { values, errors, touched, handleBlur, handleChange, handleSubmit, setFieldValue, setFieldTouched } = useFormik({
        initialValues: initialValues,
        validationSchema: AddUserSchema,
        enableReinitialize: true,

        onSubmit: (values, action) => {
            setSpinner(true)
            dispatch(addUser({ 'values': values })).then((res) => addUpdateHandler({
                res: res.payload,
                action,
                navigate,
                success_toast: `${t('user.user')} ${t('common.success_add')}`,
                wrong_toast: `${t('common.wrong')}`,
                navigate_route: '',
                spinner: setSpinner,
                toggle: () => {
                    dispatch(toggleAddModal())
                    dispatch(toggleBool())
                },
            }))
        },
    })

    useEffect(() => {
        setLoading(true)
        const pageable = { 'role_filter': 'next' }
        Promise.all([
            dispatch(userRoleList({ pageable, navigate })),
        ]).finally(() => setLoading(false));
    }, [])

    return (
        <>
            <div className="container-fluid">
                <div className='layout'>
                    {loading ?
                        <div className="row">
                            <div className="col-md-12">
                                <Loader />
                            </div>
                        </div>
                        :
                        <Form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
                            <div className="row">

                                <div className="col-md-3">
                                    <SelectBox
                                        label_name={t('user.role')}
                                        mandatory={true}
                                        placeholder=''
                                        disabled={true}
                                        option={role_option}
                                        name='role_id'
                                        value={values.role_id}
                                        onChange={(e) =>
                                            setFieldValue('role_id', e)
                                        }
                                        onBlur={() => setFieldTouched("role_id", true)}
                                        filterOption={(input, option) =>
                                            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                        }
                                        error={errors.role_id && touched.role_id ? (<span className='field_error'>{errors.role_id}</span>) : null}
                                    />
                                </div>

                                <div className="col-md-3">
                                    <Text
                                        label_name={t('user.fname')}
                                        mandatory={true}
                                        placeholder=''
                                        disabled={false}
                                        name='fname'
                                        value={values.fname}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={errors.fname && touched.fname ? (<span className='field_error'>{errors.fname}</span>) : null}
                                    />
                                </div>

                                <div className="col-md-3">
                                    <Text
                                        label_name={t('user.lname')}
                                        mandatory={true}
                                        placeholder=''
                                        disabled={false}
                                        name='lname'
                                        value={values.lname}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={errors.lname && touched.lname ? (<span className='field_error'>{errors.lname}</span>) : null}
                                    />
                                </div>

                                <div className="col-md-3">
                                    <SelectBox
                                        label_name={t('user.gender')}
                                        mandatory={true}
                                        placeholder=''
                                        disabled={false}
                                        option={gender_option}
                                        name='gender'
                                        value={values.gender}
                                        onChange={(e) =>
                                            setFieldValue('gender', e)
                                        }
                                        onBlur={() => setFieldTouched("gender", true)}
                                        filterOption={(input, option) =>
                                            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                        }
                                        error={errors.gender && touched.gender ? (<span className='field_error'>{errors.gender}</span>) : null}
                                    />
                                </div>

                                <div className="col-md-3">
                                    <DatePicker
                                        label_name={t('user.dob')}
                                        mandatory={false}
                                        placeholder=''
                                        name='dob'
                                        value={values.dob}
                                        onChange={(date, dateString) => setFieldValue('dob', dateString)}
                                        onBlur={() => setFieldTouched('dob', true)}
                                        error={errors.dob && touched.dob ? (<span className='field_error'>{errors.dob}</span>) : null}
                                    />
                                </div>

                                <div className="col-md-3">
                                    <Text
                                        label_name={t('user.username')}
                                        mandatory={true}
                                        placeholder=''
                                        disabled={false}
                                        name='username'
                                        value={values.username}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={errors.username && touched.username ? (<span className='field_error'>{errors.username}</span>) : null}
                                    />
                                </div>

                                <div className="col-md-3">
                                    <Password
                                        label_name={t('user.password')}
                                        mandatory={true}
                                        placeholder=''
                                        disabled={false}
                                        name='password'
                                        value={values.password}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        showPassword={showPassword}
                                        setShowPassword={setShowPassword}
                                        error={errors.password && touched.password ? (<span className='field_error'>{errors.password}</span>) : null}
                                    />
                                </div>

                                <div className="col-md-3">
                                    <Password
                                        label_name={t('user.confirm_password')}
                                        mandatory={true}
                                        placeholder=''
                                        disabled={false}
                                        name='confirmPassword'
                                        value={values.confirmPassword}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        showPassword={showConfirmPassword}
                                        setShowPassword={setShowConfirmPassword}
                                        error={errors.confirmPassword && touched.confirmPassword ? (<span className='field_error'>{errors.confirmPassword}</span>) : null}
                                    />
                                </div>

                                <div className="col-md-3">
                                    <Number
                                        label_name={t('user.mobile')}
                                        mandatory={true}
                                        placeholder=''
                                        disabled={false}
                                        name='mobile'
                                        value={values.mobile}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={errors.mobile && touched.mobile ? (<span className='field_error'>{errors.mobile}</span>) : null}
                                    />
                                </div>

                                <div className="col-md-3">
                                    <Email
                                        label_name={t('user.email')}
                                        mandatory={true}
                                        placeholder=''
                                        disabled={false}
                                        name='email'
                                        value={values.email}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={errors.email && touched.email ? (<span className='field_error'>{errors.email}</span>) : null}
                                    />
                                </div>

                                <div className="col-md-3">
                                    <File
                                        label_name={t('user.profile')}
                                        mandatory={false}
                                        disabled={false}
                                        name='profile'
                                        onChange={(e) =>
                                            setFieldValue('profile', e.currentTarget.files[0])
                                        }
                                        onBlur={handleBlur}
                                        error={errors.profile && touched.profile ? (<span className='field_error'>{errors.profile}</span>) : null}
                                        accept="image/jpg, image/jpeg, image/png"
                                    />
                                </div>

                                <div className="col-md-3">
                                    <SelectStatusBox
                                        label_name={t('user.status')}
                                        mandatory={true}
                                        placeholder=''
                                        disabled={true}
                                        name='status'
                                        value={values.status}
                                        onChange={(e) =>
                                            setFieldValue('status', e)
                                        }
                                        onBlur={() => setFieldTouched("status", true)}
                                        error={errors.status && touched.status ? (<span className='field_error'>{errors.status}</span>) : null}
                                    />
                                </div>

                                <div className="col-md-12">
                                    <div className="d-flex justify-content-end">
                                        <div className="col-lg-2">
                                            <SaveButton
                                                button_name={t('common.submit')}
                                                spinner={spinner}
                                            />
                                        </div>
                                    </div>
                                </div>

                            </div>
                        </Form>
                    }
                </div>
            </div>
        </>
    )
}